import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import {
  AdminApi,
  AdminBlogPost,
  AdminBlogPostStatus,
  AdminBlogSharingTypeOption,
  AdminBlogTopicOption,
} from '../../services/adminApi'
import { formatDbDateTimeVi } from '../../utils/dbDateDisplay'

const statusLabel = (status: string) => {
  if (status === 'ACTIVE') return 'Đang hiện'
  if (status === 'INACTIVE') return 'Ẩn / nháp'
  return status || '—'
}

const statusClass = (status: string) => {
  if (status === 'ACTIVE') return 'bg-emerald-100 text-emerald-800'
  return 'bg-slate-100 text-slate-700'
}

const AdminPostsPage = () => {
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState<AdminBlogPost[]>([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [total, setTotal] = useState(0)
  const [qInput, setQInput] = useState('')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<AdminBlogPostStatus | ''>('')
  const [topicId, setTopicId] = useState<number | ''>('')
  const [sharingTypeId, setSharingTypeId] = useState<number | ''>('')
  const [topics, setTopics] = useState<AdminBlogTopicOption[]>([])
  const [sharingTypes, setSharingTypes] = useState<AdminBlogSharingTypeOption[]>([])
  const [deleting, setDeleting] = useState<AdminBlogPost | null>(null)
  const [actingId, setActingId] = useState<number | null>(null)

  useEffect(() => {
    ;(async () => {
      try {
        const res = await AdminApi.getBlogPostsMeta()
        setTopics(res.data.topics || [])
        setSharingTypes(res.data.sharingTypes || [])
      } catch {
        toast.error('Không tải được danh mục topic / loại chia sẻ')
      }
    })()
  }, [])

  const loadData = useCallback(
    async (overrides?: {
      page?: number
      search?: string
      status?: AdminBlogPostStatus | ''
      topic_id?: number | ''
      sharing_type_id?: number | ''
    }) => {
      const nextPage = overrides?.page ?? page
      const nextSearch = overrides?.search ?? search
      const nextStatus = overrides?.status ?? status
      const nextTopicId = overrides?.topic_id ?? topicId
      const nextSharingTypeId = overrides?.sharing_type_id ?? sharingTypeId

      setLoading(true)
      try {
        const res = await AdminApi.listBlogPosts({
          page: nextPage,
          limit: 20,
          search: nextSearch || undefined,
          status: nextStatus || undefined,
          topic_id: nextTopicId === '' ? undefined : Number(nextTopicId),
          sharing_type_id: nextSharingTypeId === '' ? undefined : Number(nextSharingTypeId),
        })
        setItems(res.data.items)
        setTotal(res.data.pagination.total)
        setTotalPages(res.data.pagination.totalPages)
      } catch {
        toast.error('Không tải được danh sách bài viết')
      } finally {
        setLoading(false)
      }
    },
    [page, search, status, topicId, sharingTypeId]
  )

  useEffect(() => {
    void loadData({ page })
  }, [page, loadData])

  const applyFilters = () => {
    setSearch(qInput.trim())
    if (page !== 1) setPage(1)
    else void loadData({ page: 1, search: qInput.trim() })
  }

  const resetFilters = () => {
    setQInput('')
    setSearch('')
    setStatus('')
    setTopicId('')
    setSharingTypeId('')
    if (page !== 1) setPage(1)
    else void loadData({ page: 1, search: '', status: '', topic_id: '', sharing_type_id: '' })
  }

  const confirmDelete = async () => {
    if (!deleting) return
    setActingId(deleting.id)
    try {
      await AdminApi.deleteBlogPost(deleting.id)
      toast.success('Đã xóa bài viết')
      setDeleting(null)
      void loadData()
    } catch {
      toast.error('Xóa bài viết thất bại')
    } finally {
      setActingId(null)
    }
  }

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-m2-on-surface">Quản lý bài viết</h1>
          <p className="text-sm text-m2-on-surface-variant">
            Blog độc lập — không gắn khóa học / bài test. Tổng: {total}
          </p>
        </div>
        <Link
          to="/admin/posts/new"
          className="inline-flex items-center gap-2 rounded-lg bg-emerald-800 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-900"
        >
          <PlusIcon className="h-4 w-4" />
          Thêm bài viết
        </Link>
      </div>

      <div className="flex flex-wrap gap-2 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <div className="relative min-w-[200px] flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
            placeholder="Tìm theo tiêu đề, mô tả, tác giả..."
            className="w-full rounded-lg border border-slate-300 py-2 pl-9 pr-3 text-sm"
          />
        </div>
        <select
          value={topicId}
          onChange={(e) => setTopicId(e.target.value ? Number(e.target.value) : '')}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
        >
          <option value="">Tất cả topic</option>
          {topics.map((t) => (
            <option key={t.id} value={t.id}>
              {t.title}
            </option>
          ))}
        </select>
        <select
          value={sharingTypeId}
          onChange={(e) => setSharingTypeId(e.target.value ? Number(e.target.value) : '')}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
        >
          <option value="">Tất cả loại chia sẻ</option>
          {sharingTypes.map((s) => (
            <option key={s.id} value={s.id}>
              {s.type}
            </option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => setStatus((e.target.value as AdminBlogPostStatus | '') || '')}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
        >
          <option value="">Tất cả trạng thái</option>
          <option value="ACTIVE">Đang hiện</option>
          <option value="INACTIVE">Ẩn / nháp</option>
        </select>
        <button
          type="button"
          onClick={applyFilters}
          className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-900"
        >
          Lọc
        </button>
        <button
          type="button"
          onClick={resetFilters}
          className="rounded-lg border border-slate-300 px-4 py-2 text-sm hover:bg-slate-50"
        >
          Xóa lọc
        </button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full text-left text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-3">Bài viết</th>
              <th className="px-3 py-3">Topic</th>
              <th className="px-3 py-3">Trạng thái</th>
              <th className="px-3 py-3">Lượt xem</th>
              <th className="px-3 py-3">Cập nhật</th>
              <th className="px-3 py-3 text-right">Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center text-slate-500">
                  Đang tải...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center text-slate-500">
                  Chưa có bài viết nào
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50/80">
                  <td className="px-3 py-3">
                    <div className="flex items-start gap-3">
                      {item.avatarUrl ? (
                        <img
                          src={item.avatarUrl}
                          alt=""
                          className="h-12 w-16 shrink-0 rounded object-cover"
                        />
                      ) : (
                        <div className="flex h-12 w-16 shrink-0 items-center justify-center rounded bg-slate-100 text-xs text-slate-400">
                          No img
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900">{item.title}</div>
                        <div className="text-xs text-slate-500">
                          #{item.id} · {item.author || '—'}
                          {item.isFeatured ? ' · Nổi bật' : ''}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-slate-700">{item.topicTitle || '—'}</td>
                  <td className="px-3 py-3">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusClass(item.status)}`}
                    >
                      {statusLabel(item.status)}
                    </span>
                  </td>
                  <td className="px-3 py-3 tabular-nums text-slate-700">{item.totalView}</td>
                  <td className="px-3 py-3 text-slate-600">
                    {formatDbDateTimeVi(item.updatedAt)}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex justify-end gap-1">
                      <Link
                        to={`/admin/posts/${item.id}/edit`}
                        className="rounded p-1.5 text-slate-600 hover:bg-slate-100"
                        title="Sửa"
                      >
                        <PencilSquareIcon className="h-5 w-5" />
                      </Link>
                      <button
                        type="button"
                        title="Xóa"
                        onClick={() => setDeleting(item)}
                        className="rounded p-1.5 text-red-600 hover:bg-red-50"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="rounded border border-slate-300 px-3 py-1.5 text-sm disabled:opacity-40"
          >
            Trước
          </button>
          <span className="text-sm text-slate-600">
            Trang {page}/{totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="rounded border border-slate-300 px-3 py-1.5 text-sm disabled:opacity-40"
          >
            Sau
          </button>
        </div>
      )}

      {deleting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
            <h2 className="text-lg font-semibold text-slate-900">Xóa bài viết?</h2>
            <p className="mt-2 text-sm text-slate-600">
              Soft-delete bài <strong>{deleting.title}</strong>. Dữ liệu lượt xem/like được giữ lại.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleting(null)}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm"
              >
                Hủy
              </button>
              <button
                type="button"
                disabled={actingId === deleting.id}
                onClick={() => void confirmDelete()}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                Xóa
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminPostsPage
